import { SelectChangeEvent } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaDropDown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown.tsx";
import { ModelDropdownKey } from "../../../../../components/common/DropDowns/TextToImageModelsDropdown/NewTextToImageModelsDropdown.tsx";
import {
	getModelDefaultLabel,
	getModelKey,
	getModelName,
	getModelVersion,
} from "../../../../../components/common/DropDowns/TextToImageModelsDropdown/TextToImageModelsDropdown.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import { OrgFeatures } from "../../../../../models/billing.ts";
import iframeStore from "../../../iframe-store.tsx";

const IframeImageGenerationFoundationModels = () => {
	const { iframeForm, handleFormChange } = iframeStore;
	const { t } = useTranslation("translation", {
		keyPrefix: "newIframes.editor.form.imageGenerationConfig.foundationModels",
	});
	const { textToImageStore, authStore } = useAppStore();
	const [genModelsOptions, setGenModelsOptions] = useState<DropDownItem[]>([]);
	const selectedModelsKeys: string[] = Object.entries(
		iframeForm?.config.image_generation_config?.enabled_foundation_models ?? {},
	).flatMap(([model, versions]) => Object.keys(versions).map((version) => getModelKey(model, version)));

	useEffect(() => {
		const createModelsOptions = async (): Promise<DropDownItem[]> => {
			const models = await textToImageStore.getModels();
			const options = Object.entries(models).flatMap(([modelType, model]) =>
				model.versions.map((version: string) => ({
					key: getModelDefaultLabel(modelType, version),
					value: getModelKey(modelType, version),
				})),
			);

			return options.filter((option) => option.value !== "fibo").sort(customFoundationModelsSort);
		};

		createModelsOptions().then(setGenModelsOptions);
	}, [iframeForm?.config.image_generation_config?.enabled_foundation_models]);

	function customFoundationModelsSort(item1: DropDownItem, item2: DropDownItem) {
		const order: ModelDropdownKey[] = ["fibo", "fibo_lite", "base_3.2", "fast_2.3", "fast_2.2", "hd_2.2"];
		return order.indexOf(item1.value) - order.indexOf(item2.value);
	}

	const handleChange = (e: SelectChangeEvent<string[]>) => {
		const updatedGenModels = (e.target.value as string[]).reduce((acc, key) => {
			const modelName = getModelName(key);
			const modelVersion = getModelVersion(key);
			const displayName =
				iframeForm.config.image_generation_config?.enabled_foundation_models?.[modelName]?.[modelVersion] ??
				getModelDefaultLabel(modelName, modelVersion);
			acc[modelName] = { ...acc?.[modelName], [modelVersion]: displayName };
			return acc;
		}, {} as IframeImageGenerationFoundationModels);

		handleFormChange("config", {
			...iframeForm.config,
			image_generation_config: {
				...(iframeForm.config.image_generation_config ?? {}),
				enabled_foundation_models: updatedGenModels,
			},
		});
	};

	const handleDelete = (keyToDelete: string): void => {
		const updatedGenModels = { ...iframeForm?.config.image_generation_config?.enabled_foundation_models };
		delete updatedGenModels?.[getModelName(keyToDelete)]?.[getModelVersion(keyToDelete)];

		handleFormChange("config", {
			...iframeForm.config,
			image_generation_config: {
				...(iframeForm.config.image_generation_config ?? {}),
				enabled_foundation_models: updatedGenModels,
			},
		});
	};

	const handleEditLabel = (keyToEdit: string, newValue: string): void => {
		if (newValue) {
			const updatedGenModels = { ...iframeForm?.config.image_generation_config?.enabled_foundation_models };
			updatedGenModels[getModelName(keyToEdit)][getModelVersion(keyToEdit)] = newValue as string;

			handleFormChange("config", {
				...iframeForm.config,
				image_generation_config: {
					...(iframeForm.config.image_generation_config ?? {}),
					enabled_foundation_models: updatedGenModels,
				},
			});
		}
	};

	return (
		<BriaDropDown
			value={selectedModelsKeys}
			items={genModelsOptions.map((item) => ({
				key:
					iframeForm.config.image_generation_config?.enabled_foundation_models?.[getModelName(item.value)]?.[
						getModelVersion(item.value)
					] ?? item.key,
				value: item.value,
			}))}
			loading={textToImageStore.loadingModels || authStore.isLoadingOrgSubscriptions}
			onChange={handleChange}
			handleDelete={handleDelete}
			handleEditItem={authStore.isFeatureEnabled(OrgFeatures.IFRAME_WHITE_LABEL) ? handleEditLabel : undefined}
			placeholder={t("placeholder")}
			multiple
		/>
	);
};

export default observer(IframeImageGenerationFoundationModels);

export type DisplayName = string;
export type IframeImageGenerationFoundationModels = { [model: string]: { [version: string]: DisplayName } };
